import {
    type CostReport,
    type EmploymentType,
    type EntityFilter,
    GetCostReportsParams,
    TimeEntryType,
    formatNumber as fmtNum,
    getEmployeeName,
    getEmploymentTypes,
    getRange,
    monthNames,
    parseDateString,
    throttle,
    timeEntryTypeColor,
    toDateString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import type { BreakdownItem } from "@pentacode/ui/charts";
import { breakdownChart, chartSeriesColors, formatCurrency, lineChart } from "@pentacode/ui/charts";
import "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { guard } from "lit/directives/guard.js";
import { popover } from "../../directives/popover";
import { alert } from "../../elements/alert-dialog";
import { Checkbox } from "../../elements/checkbox";
import "../../elements/entity-filters";
import type { EntityFiltersEl } from "../../elements/entity-filters";
import "../../elements/entity-multi-select";
import type { EntityMultiSelect } from "../../elements/entity-multi-select";
import { app } from "../../init";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import { colors } from "../../styles/config";
import { withUniqueNames } from "./report-charts";
import type { EmployeeShortInfo } from "./types";

const formatNumber = (val: number) => fmtNum(val, 0);

interface ChartSeries {
    name: string;
    data: number[];
    color: string;
    dashed?: boolean;
}

type SelectedEmployee = EmployeeShortInfo & { retired?: boolean };

const components = [
    {
        name: "Basislohn",
        key: "base",
        icon: "icon.time-entry.work",
        color: colors.green,
    },
    {
        name: "Pausen",
        key: "breaks",
        icon: "icon.object.break",
        color: colors.orange,
    },
    {
        name: "Provision",
        key: "commission",
        icon: "icon.object.currency",
        color: colors.red,
    },
    {
        name: "Zuschläge",
        key: "bonuses",
        icon: "icon.object.bonus",
        color: colors.purple,
    },
    {
        name: "Mitarbeiteressen",
        key: "meals",
        icon: "icon.object.meal",
        color: colors.teal,
    },
];

const employmentTypes = getEmploymentTypes();

const timeEntryTypes = [
    TimeEntryType.Work,
    TimeEntryType.Vacation,
    TimeEntryType.Sick,
    TimeEntryType.ChildSick,
    TimeEntryType.SickInKUG,
    TimeEntryType.CompDay,
    TimeEntryType.HourAdjustment,
    TimeEntryType.VacationAdjustment,
];

@customElement("ptc-reports-costs")
export class Reports extends Routing(StateMixin(LitElement)) {
    routePattern = /^reports\/costs/;

    get routeTitle() {
        return "Personalkostenbericht";
    }

    @state()
    private _loading = false;

    @state()
    private _reports: CostReport[] = [];

    @state()
    private _selectedEmployee: SelectedEmployee | null = null;

    @state()
    private _segmentation: "type" | "component" | "employment" | "workArea" = "workArea";

    @state()
    private _filterString = "";

    @query("#filterInput")
    private _filterInput: HTMLInputElement;

    @state()
    private _monthlySeries: ChartSeries[] = [];

    @state()
    private _costTotalItems: BreakdownItem[] = [];

    @state()
    private _highlight?: string;

    @query("ptc-entity-filters")
    private _entityFilters: EntityFiltersEl;

    @query("#totalCheckbox")
    private _totalCheckbox: Checkbox;

    @query("#ancillaryCostsCheckbox")
    private _ancillaryCostsCheckbox: Checkbox;

    @query("#typeSelector")
    private _typeSelector: EntityMultiSelect<TimeEntryType>;

    @query("#componentSelector")
    private _componentSelector: EntityMultiSelect<(typeof components)[number]>;

    @query("#employmentTypeSelector")
    private _employmentTypeSelector: EntityMultiSelect<(typeof employmentTypes)[number]>;

    private _updateFilter() {
        this._filterString = this._filterInput.value;
    }

    private _clearFilter() {
        this._filterString = this._filterInput.value = "";
    }

    private _cachedCosts = new Map<string, number>();

    handleRoute() {
        //Invalid date
        if (!this.date || !parseDateString(this.date)) {
            this.go(null, { date: toDateString(new Date()) }, true);
            return;
        }
        void this.load();
    }

    async firstUpdated() {
        // we have to wait for the company data to be loaded
        await app.loaded;
        this._entityFilters.filters =
            app.accessibleVenues.length > 1
                ? app.accessibleVenues.map((venue) => ({ type: "venue", value: venue.id }) as EntityFilter)
                : app.accessibleDepartments.map(
                      (department) =>
                          ({
                              type: "department",
                              value: department.id,
                          }) as EntityFilter
                  );
        this._typeSelector.selected = timeEntryTypes;
        this._componentSelector.selected = components;
        this._employmentTypeSelector.selected = employmentTypes;
    }

    updated(changes: Map<string, unknown>) {
        if (
            (changes.has("_reports") || changes.has("_segmentation") || changes.has("_selectedEmployee")) &&
            this._reports.length
        ) {
            this._cachedCosts.clear();
            this.requestUpdate();
            this._updateCharts();
        }
    }

    async load() {
        if (!this.year) {
            return;
        }

        const range = getRange(`${this.year}-01-01` as DateString, "year");
        const from = range.from;
        let to = range.to;

        const currMonth = getRange(toDateString(new Date()), "month");

        if (to > currMonth.to) {
            to = currMonth.to;
        }

        this._loading = true;

        try {
            this._reports = await app.api.getCostReports(
                new GetCostReportsParams({
                    from,
                    to,
                    resolution: "month",
                })
            );
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _getCosts({
        type,
        component,
        month,
        employeeId,
        employmentType,
        departmentId,
        venueId,
    }: {
        type?: TimeEntryType;
        component?: (typeof components)[number]["key"];
        month?: number;
        employeeId?: number;
        employmentType?: EmploymentType;
        departmentId?: number;
        venueId?: number;
    }) {
        const includeAncillaryCosts = this._ancillaryCostsCheckbox?.checked || false;
        const cacheKey = `${includeAncillaryCosts}_${type || "all"}_${component || "all"}_${typeof month === "number" ? month : "all"}_${employeeId || "all"}_${typeof employmentType === "number" ? employmentType : "all"}_${departmentId || "all"}_${venueId || "all"}`;

        if (this._cachedCosts.has(cacheKey)) {
            return this._cachedCosts.get(cacheKey)!;
        }

        let reports = this._reports;

        if (typeof employeeId === "number") {
            reports = reports.filter((report) => report.employeeId === employeeId);
        }
        if (typeof month === "number") {
            reports = reports.filter((report) => parseDateString(report.from)?.getMonth() === month);
        }
        if (typeof employmentType === "number") {
            reports = reports.filter((report) => {
                const employee = app.getEmployee(report.employeeId);
                const contract = employee && employee.getContractForRange(report);
                return contract && employmentType === contract.employmentType;
            });
        }

        let items = reports.flatMap((report) => report.items);

        if (typeof departmentId === "number") {
            items = items.filter(
                (item) => item.positionId && app.getPosition(item.positionId)?.department?.id === departmentId
            );
        }

        if (typeof venueId === "number") {
            items = items.filter((item) => item.positionId && app.getPosition(item.positionId)?.venue?.id === venueId);
        }

        if (type) {
            items = items.filter((item) => item.type === type);
        }

        const prop = includeAncillaryCosts ? ("totalCosts" as const) : ("wages" as const);

        const costs = items.reduce((total, item) => {
            switch (component) {
                case "base":
                    return total + item.base[prop];
                case "breaks":
                    return total + item.breaks[prop];
                case "bonuses":
                    return total + item.bonuses[prop];
                case "commission":
                    return total + item.commission[prop];
                case "meals":
                    return total + item.meals[prop];
                default:
                    return (
                        total +
                        item.base[prop] +
                        item.breaks[prop] +
                        item.bonuses[prop] +
                        item.meals[prop] +
                        item.commission[prop]
                    );
            }
        }, 0);

        this._cachedCosts.set(cacheKey, costs);
        return costs;
    }

    private _getCostSeriesByComponent() {
        const employeeId = (this._selectedEmployee && this._selectedEmployee.id) || undefined;

        return this._componentSelector.selected.map(({ name, key, color }) => ({
            name,
            data: monthNames.map((_n, month) => this._getCosts({ component: key, employeeId: employeeId, month })),
            color,
        }));
    }

    private _getCostSeriesByType() {
        const employeeId = (this._selectedEmployee && this._selectedEmployee.id) || undefined;

        return this._typeSelector.selected.map((type) => ({
            name: app.localized.timeEntryTypeLabel({ type }),
            data: monthNames.map((_n, month) => this._getCosts({ type, employeeId, month })),
            color: timeEntryTypeColor(type) || colors.brown,
        }));
    }

    private _getCostSeriesByEmploymentType() {
        const employeeId = (this._selectedEmployee && this._selectedEmployee.id) || undefined;

        return this._employmentTypeSelector.selected.map(({ type, label, color }) => ({
            name: label,
            data: monthNames.map((_n, month) =>
                this._getCosts({ employeeId: employeeId, month, employmentType: type })
            ),
            color,
        }));
    }

    private _getCostSeriesByWorkarea() {
        const employee = (this._selectedEmployee && this._selectedEmployee.id) || undefined;
        const filters = this._entityFilters.filters.filter((f) => f.type === "department" || f.type === "venue");

        let venueCount = 0;
        const series = filters.map((filter) => {
            switch (filter.type) {
                case "department": {
                    const department = app.getDepartment(filter.value).department!;
                    return {
                        name: department.name,
                        data: monthNames.map((_n, month) =>
                            this._getCosts({ employeeId: employee, month, departmentId: department.id })
                        ),
                        color: colors[department.color] || department.color,
                    };
                }
                case "venue": {
                    const venue = app.getVenue(filter.value)!;
                    venueCount++;
                    return {
                        name: venue.name,
                        data: monthNames.map((_n, month) =>
                            this._getCosts({ employeeId: employee, month, venueId: venue.id })
                        ),
                        color: chartSeriesColors[(venueCount - 1) % chartSeriesColors.length],
                        dashed: true,
                    };
                }
            }
        });

        return series;
    }

    private _getMonthlySeries(series: ChartSeries[]) {
        const employeeId = this._selectedEmployee?.id;

        if (!this._totalCheckbox.checked) {
            return series;
        }

        return [
            {
                name: "Gesamt",
                data: monthNames.map((_n, month) => this._getCosts({ employeeId, month })),
                color: "var(--color-fg)",
            },
            ...series,
        ];
    }

    private _getChartDefinitionCostMonthly(series: ChartSeries[]) {
        if (!series.length) {
            return undefined;
        }

        const rows = series.flatMap(({ name, data }) =>
            data.map((value, month) => ({ x: monthNames[month].slice(0, 3), series: name, value }))
        );

        return lineChart(rows, {
            yAxis: "right",
            grouped: true,
            legend: false,
            highlight: this._highlight,
            format: formatCurrency,
            colors: Object.fromEntries(series.map(({ name, color }) => [name, color])),
            dashed: series.filter((s) => s.dashed).map((s) => s.name),
        });
    }

    private _getLegendItems() {
        const items = this._monthlySeries.map(({ name, color }) => ({ label: name, color }));
        for (const { label, color, value } of this._costTotalItems) {
            if (Math.round(value) && !items.some((item) => item.label === label)) {
                items.push({ label, color: color! });
            }
        }
        return items;
    }

    private _getCostTotalItems(monthlySeries: ChartSeries[]) {
        const items: BreakdownItem[] = monthlySeries.map(({ name, data, color }) => ({
            label: name,
            value: data.reduce((total, val) => total + val, 0),
            color,
        }));

        if (this._totalCheckbox.checked) {
            const total = this._getCosts({ employeeId: this._selectedEmployee?.id });
            const other = total - items.reduce((sum, item) => sum + item.value, 0);
            if (Math.round(other) > 0) {
                items.push({ label: "Sonstige", value: other, color: chartSeriesColors[7] });
            }
        }

        return items;
    }

    private _updateCharts = throttle(() => {
        const series: ChartSeries[] = withUniqueNames(
            this._segmentation === "employment"
                ? this._getCostSeriesByEmploymentType()
                : this._segmentation === "workArea"
                  ? this._getCostSeriesByWorkarea()
                  : this._segmentation === "type"
                    ? this._getCostSeriesByType()
                    : this._getCostSeriesByComponent(),
            ["Gesamt", "Sonstige"]
        );

        this._monthlySeries = this._getMonthlySeries(series);
        this._costTotalItems = this._getCostTotalItems(series);
    }, 100);

    private _yearSelected(e: Event) {
        const date = parseDateString(this.date) || new Date();
        date.setFullYear(parseInt((e.target as HTMLSelectElement).value));
        this.go(null, { date: toDateString(date) });
    }

    private _selectEmployee(employee: SelectedEmployee | null) {
        this._selectedEmployee = employee;
        this.renderRoot.querySelector(".charts")!.scrollIntoView();
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            :host {
                display: grid;
                grid-template-columns: 200px 1fr;
            }

            .header {
                border-bottom: solid 1px var(--shade-2);
            }

            .main {
                position: relative;
            }

            .scroller {
                overflow: auto;
            }

            .charts {
                position: sticky;
                left: 0;
                padding: 0 1em;
                display: flex;
                align-items: center;
                z-index: 10;
            }

            .charts-cost-total {
                flex: 1;
            }

            .charts-cost-monthly {
                flex: 3;
            }

            .charts-title {
                font-size: var(--font-size-large);
                padding: 0.5em;
                font-weight: 600;
                text-align: center;
            }

            .table {
                margin: 0 0.5em;
            }

            .row {
                display: grid;
                grid-template-columns: 12em repeat(13, 1fr);
                background: var(--color-bg);
            }

            .row:not(:last-child) {
                border-bottom: solid 1px var(--shade-1);
            }

            .column {
                padding: 0.5em;
                text-align: right;
                min-width: 0;
            }

            .column:not(:last-child) {
                border-right: solid 1px var(--shade-1);
            }

            .header-row {
                position: sticky;
                top: 0;
                z-index: 1;
            }

            .header-column {
                text-align: left;
                position: sticky;
                left: 0.5em;
                background: inherit;
                border-radius: inherit;
            }

            .header-row > .column {
                font-weight: 600;
                border: none;
                text-align: center;
            }

            .header-row > .header-column {
                padding-left: 0;
                padding-bottom: 0.3em;
            }

            .filter-input input {
                font-weight: normal;
                padding-top: 0.4em;
                padding-bottom: 0.4em;
            }

            .row.total-row > .column {
                font-weight: bold;
            }

            .total-column {
                font-weight: 600;
            }

            .row:not(.header-row):hover:not([active]) {
                background: #f1f1f1;
                cursor: pointer;
            }

            .row[active] {
                border-radius: var(--border-radius);
                border-color: transparent;
                background: var(--color-primary);
                color: var(--color-bg);
                position: sticky;
                top: 3.5em;
                bottom: 0.5em;
                z-index: 2;
                font-weight: bold;
            }
        `,
    ];

    render() {
        const years: number[] = [];
        for (let year = new Date().getFullYear(); year > 2010; year--) {
            years.push(year);
        }

        const displayedEmployees = app.accessibleEmployees.filter((e) =>
            getEmployeeName(e, "lastNameFirst").toLowerCase().includes(this._filterString.toLowerCase())
        );

        return html`
            <div class="fullbleed vertical layout">
                <div class="header horizontal spacing center-aligning layout padded-medium noprint">
                    <div class="tabs">
                        <button
                            ?active=${this._segmentation === "workArea"}
                            @click=${() => (this._segmentation = "workArea")}
                        >
                            Arbeitsbereiche
                        </button>
                        <button ?active=${this._segmentation === "type"} @click=${() => (this._segmentation = "type")}>
                            Status
                        </button>
                        <button
                            ?active=${this._segmentation === "component"}
                            @click=${() => (this._segmentation = "component")}
                        >
                            Komponenten
                        </button>
                        <button
                            ?active=${this._segmentation === "employment"}
                            @click=${() => (this._segmentation = "employment")}
                        >
                            Anstellung
                        </button>
                    </div>

                    <div class="stretch"></div>

                    <ptc-checkbox-button
                        .label=${html`<ptc-ui-icon name="icon.toggle.ancillary-costs"></ptc-ui-icon> Inkl. Nebenkosten`}
                        id="ancillaryCostsCheckbox"
                        buttonClass="small slim transparent ghost"
                        checked
                        @change=${() => {
                            this.requestUpdate();
                            this._updateCharts();
                        }}
                    ></ptc-checkbox-button>

                    <ptc-checkbox-button
                        .label=${html`<ptc-ui-icon name="icon.object.revenue-overview"></ptc-ui-icon> Gesamt`}
                        id="totalCheckbox"
                        buttonClass="small slim transparent ghost"
                        checked
                        @change=${this._updateCharts}
                    ></ptc-checkbox-button>

                    <select .value=${this.year?.toString()} @change=${this._yearSelected} class="slim">
                        ${years.map((year) => html` <option .value=${year.toString()}>${year}</option> `)}
                    </select>
                </div>

                <div class="scroller stretch">
                    <div class="charts">
                        <div class="charts-cost-total">
                            <div class="charts-title">
                                ${(this._selectedEmployee &&
                                    getEmployeeName(this._selectedEmployee, "lastNameFirst")) ||
                                "Alle Mitarbeiter"}
                            </div>
                            <ptc-ui-chart
                                label="Personalkosten im gewählten Zeitraum nach Kostenart"
                                .height=${300}
                                .definition=${guard([this._costTotalItems, this._highlight], () =>
                                    breakdownChart(this._costTotalItems, {
                                        format: formatCurrency,
                                        legend: false,
                                        highlight: this._highlight,
                                    })
                                )}
                            ></ptc-ui-chart>
                            <ptc-ui-chart-legend
                                .items=${guard([this._monthlySeries, this._costTotalItems], () =>
                                    this._getLegendItems()
                                )}
                                .highlight=${this._highlight}
                                @chart-legend-highlight=${(e: CustomEvent<{ label: string | null }>) =>
                                    (this._highlight = e.detail.label ?? undefined)}
                            ></ptc-ui-chart-legend>
                        </div>
                        <div class="charts-cost-monthly">
                            <ptc-ui-chart
                                label="Monatliche Personalkosten nach Kostenart im gewählten Jahr"
                                .height=${400}
                                .definition=${guard([this._monthlySeries, this._highlight], () =>
                                    this._getChartDefinitionCostMonthly(this._monthlySeries)
                                )}
                            ></ptc-ui-chart>
                        </div>
                    </div>

                    <div
                        class="padded border-bottom"
                        style="position: relative; z-index: 100;"
                        ?hidden=${this._segmentation !== "workArea"}
                    >
                        <ptc-entity-filters
                            .filterTypes=${["venue", "department"] as const}
                            .icon=${"icon.feature.reports"}
                            .hideEmployeeCount=${true}
                            emptyIcon="icon.status.none-chosen"
                            emptyLabel="Keine Arbeitsbereiche gewählt"
                            noOptionsLabel="Keine Arbeitsbereiche gefunden."
                            @change=${this._updateCharts}
                        ></ptc-entity-filters>
                    </div>

                    <div
                        class="padded border-bottom horizontal center-aligning layout"
                        style="position: relative; z-index: 100;"
                        ?hidden=${this._segmentation !== "type"}
                    >
                        <ptc-ui-icon name="icon.feature.reports" class="icon left-margined"></ptc-ui-icon>
                        <ptc-entity-multi-select
                            id="typeSelector"
                            .existing=${timeEntryTypes}
                            .getLabel=${(type: TimeEntryType) => app.localized.timeEntryTypeLabel({ type })}
                            .getColor=${(type: TimeEntryType) => timeEntryTypeColor(type)}
                            .getIcon=${(type: TimeEntryType) => app.localized.timeEntryTypeIcon(type)}
                            @change=${this._updateCharts}
                            emptyIcon="icon.status.none-chosen"
                            emptyLabel="Keine Stati gewählt"
                            noOptionsLabel="Keine Stati gefunden."
                            style="border: none"
                            class="small"
                        ></ptc-entity-multi-select>
                    </div>

                    <div
                        class="padded border-bottom horizontal center-aligning layout"
                        style="position: relative; z-index: 100;"
                        ?hidden=${this._segmentation !== "component"}
                    >
                        <ptc-ui-icon name="icon.feature.reports" class="icon left-margined"></ptc-ui-icon>
                        <ptc-entity-multi-select
                            id="componentSelector"
                            .existing=${components}
                            .getLabel=${(component: (typeof components)[number]) => component.name}
                            .getColor=${(component: (typeof components)[number]) => component.color}
                            .getIcon=${(component: (typeof components)[number]) => component.icon}
                            .getId=${(component: (typeof components)[number]) => component.key}
                            @change=${this._updateCharts}
                            emptyIcon="icon.status.none-chosen"
                            emptyLabel="Keine Komponenten gewählt"
                            noOptionsLabel="Keine Komponenten gefunden."
                            style="border: none"
                            class="small"
                        ></ptc-entity-multi-select>
                    </div>

                    <div
                        class="padded border-bottom horizontal center-aligning layout"
                        style="position: relative; z-index: 100;"
                        ?hidden=${this._segmentation !== "employment"}
                    >
                        <ptc-ui-icon name="icon.feature.reports" class="icon left-margined"></ptc-ui-icon>
                        <ptc-entity-multi-select
                            id="employmentTypeSelector"
                            .existing=${employmentTypes}
                            .getLabel=${(type: (typeof employmentTypes)[number]) => type.label}
                            .getColor=${(type: (typeof employmentTypes)[number]) => type.color}
                            .getIcon=${() => "icon.object.contract"}
                            .getId=${(type: (typeof employmentTypes)[number]) => type.type}
                            @change=${this._updateCharts}
                            emptyIcon="icon.status.none-chosen"
                            emptyLabel="Keine Anstellungen gewählt"
                            noOptionsLabel="Keine Anstellungen gefunden."
                            style="border: none"
                            class="small"
                        ></ptc-entity-multi-select>
                    </div>

                    <div class="table">
                        <div class="row header-row">
                            <div class="column header-column">
                                <div class="filter-input right icon input">
                                    <input
                                        id="filterInput"
                                        type="text"
                                        placeholder="Suchen..."
                                        @input=${this._updateFilter}
                                    />
                                    ${this._filterString
                                        ? html`<ptc-ui-icon
                                              name="icon.action.clear"
                                              class="click icon"
                                              @click=${this._clearFilter}
                                          ></ptc-ui-icon>`
                                        : html`<ptc-ui-icon
                                              name="icon.action.search"
                                              class="icon"
                                              @click=${this._clearFilter}
                                          ></ptc-ui-icon>`}
                                </div>
                            </div>

                            ${monthNames.map(
                                (n) => html`
                                    <div class="column vertical end-justifying layout">
                                        <div>${n.slice(0, 3)}</div>
                                    </div>
                                `
                            )}

                            <div class="column vertical end-justifying layout total-column"><div>Gesamt</div></div>
                        </div>

                        <div
                            class="row total-row"
                            ?active=${!this._selectedEmployee}
                            @click=${() => this._selectEmployee(null)}
                        >
                            <div class="column header-column">Alle Mitarbeiter</div>

                            ${monthNames.map(
                                (_n, month) => html`
                                    <div class="column">${formatNumber(this._getCosts({ month }))}</div>
                                `
                            )}

                            <div class="column total-column">${formatNumber(this._getCosts({}))}</div>
                        </div>

                        ${displayedEmployees.map((employee) => {
                            const data = monthNames.map((_n, month) =>
                                this._getCosts({
                                    employeeId: employee.id,
                                    month,
                                })
                            );

                            if (data.every((d) => !d)) {
                                return null;
                            }

                            return html`
                                <div
                                    class="row"
                                    ?active=${this._selectedEmployee && this._selectedEmployee.id === employee.id}
                                    @click=${() => this._selectEmployee(employee)}
                                    style=${employee.active ? "" : "opacity: 0.75"}
                                    ${employee.active
                                        ? nothing
                                        : popover(html`Mitarbeiter ausgeschieden`, {
                                              class: "tooltip",
                                              trigger: "hover",
                                          })}
                                >
                                    <div class="column header-column ellipsis">
                                        ${getEmployeeName(employee, "lastNameFirst")}
                                    </div>

                                    ${data.map((val) => html` <div class="column">${formatNumber(val)}</div> `)}

                                    <div class="column total-column">
                                        ${formatNumber(data.reduce((total, m) => total + m))}
                                    </div>
                                </div>
                            `;
                        })}
                    </div>
                </div>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
